import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { LoggedOut, api } from "./api/client";
import type { DashboardView } from "./api/types";
import { Banner } from "./components/Parts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useRoute } from "./lib/router";
import { LogIn } from "./screens/LogIn";
import { Now } from "./screens/Now";
import { Bots } from "./screens/Bots";
import { Tasks } from "./screens/Tasks";
import { Runs } from "./screens/Runs";

/** How often the Now state asks again. */
const REFRESH = 2000;

/**
 * Somewhere for a failure to go.
 *
 * The Kotlin discarded errors in twelve places, which is why pressing Stop looked like pressing
 * nothing. Every screen reports through this instead, and the banner is the answer to "why did that
 * do nothing".
 */
const Trouble = createContext<(error: unknown) => void>(() => {});

/**
 * A failure and who hit it.
 *
 * The source matters because the two are cleared by different things: the poll's complaint goes
 * when the poll next succeeds, but a Stop that was refused stays until it is read and dismissed.
 */
type Trouble = { message: string; from: "poll" | "action" };

export function useTrouble() {
  return useContext(Trouble);
}

/** Runs an action, reporting anything it throws rather than losing it. */
export function useAction(): [
  boolean,
  (action: () => Promise<unknown>) => void,
] {
  const [busy, setBusy] = useState(false);
  const report = useTrouble();

  const run = useCallback(
    (action: () => Promise<unknown>) => {
      setBusy(true);
      action()
        .catch(report)
        .finally(() => setBusy(false));
    },
    [report],
  );

  return [busy, run];
}

const TABS = [
  { path: "/", title: "Now" },
  { path: "/bots", title: "Bots" },
  { path: "/tasks", title: "Tasks" },
  { path: "/runs", title: "Runs" },
];

export function App() {
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [now, setNow] = useState<DashboardView | null>(null);
  const [trouble, setTrouble] = useState<Trouble | null>(null);
  const [path, navigate] = useRoute();

  const raise = useCallback((error: unknown, from: Trouble["from"]) => {
    if (error instanceof LoggedOut) {
      setSignedIn(false);
      return;
    }
    setTrouble({
      message: error instanceof Error ? error.message : String(error),
      from,
    });
  }, []);

  const report = useCallback(
    (error: unknown) => raise(error, "action"),
    [raise],
  );

  useEffect(() => {
    api.session().then(setSignedIn).catch(report);
  }, [report]);

  useEffect(() => {
    if (signedIn !== true) return undefined;

    let live = true;
    const tick = async () => {
      try {
        const view = await api.dashboard();
        if (!live) return;
        setNow(view);
        // Only the poll's own complaint. Clearing everything here meant an action's message lived
        // two seconds and then disappeared, which on screen is indistinguishable from never having
        // been shown -- the exact failure this banner exists to end.
        setTrouble((it) => (it?.from === "poll" ? null : it));
      } catch (error) {
        if (!live) return;
        // Anything at all, not just the two kinds the Kotlin knew about. A serialisation error used
        // to kill the poller permanently with nothing on screen to say so.
        raise(error, "poll");
      }
    };

    void tick();
    const timer = window.setInterval(() => void tick(), REFRESH);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [signedIn, raise]);

  if (signedIn === null) return <Booting />;
  if (!signedIn) return <LogIn onIn={() => setSignedIn(true)} />;

  return (
    <Trouble.Provider value={report}>
      <div className="flex min-h-screen flex-col">
        <header className="flex items-center gap-6 border-b px-7 py-3.5 max-sm:flex-wrap max-sm:gap-x-4 max-sm:gap-y-3 max-sm:px-4">
          <span className="font-serif text-heading font-semibold -tracking-[0.01em]">
            botty
          </span>
          <nav className="flex gap-6">
            {TABS.map((tab) => (
              <Button
                key={tab.path}
                variant="ghost"
                size="sm"
                className="rounded-none border-b-2 border-transparent px-1 aria-[current=page]:border-b-foreground aria-[current=page]:text-foreground"
                aria-current={tab.path === path ? "page" : undefined}
                onClick={() => navigate(tab.path)}
              >
                {tab.title}
              </Button>
            ))}
          </nav>
          <span className="flex-1" />
          <KillSwitchChip view={now} />
          <Button
            variant="ghost"
            onClick={() => {
              api.logOut().catch(report);
              setSignedIn(false);
            }}
          >
            Log out
          </Button>
        </header>

        {trouble ? (
          <Banner
            title="Something went wrong"
            tone="failed"
            action={
              <Button variant="ghost" onClick={() => setTrouble(null)}>
                Dismiss
              </Button>
            }
          >
            {trouble.message}
          </Banner>
        ) : null}

        <main className="flex-1 px-7 max-sm:px-4">
          <div className="mx-auto flex max-w-measure flex-col gap-6 py-6">
            <Screen path={path} now={now} navigate={navigate} />
          </div>
        </main>
      </div>
    </Trouble.Provider>
  );
}

function Screen({
  path,
  now,
  navigate,
}: {
  path: string;
  now: DashboardView | null;
  navigate: (to: string) => void;
}) {
  if (path.startsWith("/bots")) return <Bots navigate={navigate} />;
  if (path.startsWith("/tasks")) return <Tasks navigate={navigate} />;
  if (path.startsWith("/runs")) return <Runs />;
  return <Now now={now} />;
}

/**
 * Whether anything may save.
 *
 * In the running head because it is a standing fact about the deployment, not an error to discover
 * on submitting a form.
 */
function KillSwitchChip({ view }: { view: DashboardView | null }) {
  const state = view?.killSwitch;
  const [tone, word] = !state
    ? ["bg-muted-foreground", "unknown"]
    : !state.reachable
      ? ["bg-destructive", "unreachable"]
      : state.stopped
        ? ["bg-waiting", "stopped"]
        : ["bg-running", "clear"];

  return (
    <Badge
      variant="outline"
      className="h-auto gap-2 px-2 py-1"
      title={state?.detail ?? ""}
    >
      <span className={`inline-block size-2 flex-none rounded-full ${tone}`} />
      <span className="apparatus">Kill switch</span>
      <span className="font-mono text-foreground">{word}</span>
    </Badge>
  );
}

/** Two greys holding the shape of the page, so a slow first answer is not a blank screen. */
function Booting() {
  return (
    <div className="flex-1 px-7 max-sm:px-4">
      <div
        className="mx-auto flex max-w-measure flex-col gap-6 py-6"
        aria-busy="true"
        aria-label="Loading"
      >
        <Skeleton className="h-11 w-55" />
        <Skeleton className="h-45 opacity-60" />
        <Skeleton className="h-45 opacity-60" />
      </div>
    </div>
  );
}
